import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Outlet, useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { PageTransition } from '@/components/motion/page-transition';
import { useLogout } from '@/features/auth/use-logout';
import { refreshSession } from '@/lib/api/client';
import { useSession } from '@/lib/auth/session-store';
import { applyBranding } from '@/lib/branding';
import { safeStorage } from '@/lib/storage';
import { useRealtime } from '../realtime/use-realtime';
import { useMe, useNavigation } from '../session/hooks';
import { isSwitchingSession, useSupportSession, useSupportSwitching } from '../session/support';
import { CommandPalette } from './command-palette';
import { pageTitleFor } from './navigation';
import { SessionSwitchOverlay } from './session-switch-overlay';
import { Sidebar } from './sidebar';
import { SupportBanner } from './support-banner';
import { Topbar } from './topbar';

const SIDEBAR_KEY = 'an-crm.sidebar';

/** Refreshes the access token a minute before it expires (not during support: that token can't refresh). */
function useTokenRefreshTimer() {
  const { expiresAt, support } = useSession();
  useEffect(() => {
    if (!expiresAt || support) return;
    const delay = Math.max(5_000, expiresAt - Date.now() - 60_000);
    const t = setTimeout(() => void refreshSession(), delay);
    return () => clearTimeout(t);
  }, [expiresAt, support]);
}

/**
 * Ends a support session when it expires, and recovers when the token was swapped back to the
 * platform behind our back (e.g. a 401 made the API client refresh into the platform session).
 */
function useSupportLifecycle(
  meSupportExpiresAt: string | null,
  end: ReturnType<typeof useSupportSession>['end'],
) {
  const { support, expiresAt } = useSession();

  useEffect(() => {
    if (!support) return;
    const serverEnd = meSupportExpiresAt ? new Date(meSupportExpiresAt).getTime() : Infinity;
    const endsAt = Math.min(expiresAt ?? Infinity, serverEnd);
    if (!Number.isFinite(endsAt)) return;
    const t = setTimeout(
      () => void end({ reason: 'Your support session has ended.' }),
      Math.max(0, endsAt - Date.now() - 2_000),
    );
    return () => clearTimeout(t);
  }, [support, expiresAt, meSupportExpiresAt, end]);

  useEffect(() => {
    if (meSupportExpiresAt && !support && !isSwitchingSession()) {
      void end({ reason: 'Your support session has ended.', alreadyRestored: true });
    }
  }, [meSupportExpiresAt, support, end]);
}

export function AppShell() {
  const me = useMe();
  const nav = useNavigation();
  const signOut = useLogout();
  const support = useSupportSession();
  const switching = useSupportSwitching();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [collapsed, setCollapsed] = useState(() => safeStorage.get(SIDEBAR_KEY) === 'collapsed');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useTokenRefreshTimer();
  useSupportLifecycle(me.support?.expiresAt ?? null, support.end);

  const onSessionRevoked = useCallback(() => {
    if (me.support) {
      void support.end({ reason: 'The support session was revoked.' });
      return;
    }
    void signOut({ callApi: false, reason: 'This session was signed out from another device.' });
  }, [me.support, signOut, support]);
  useRealtime({ onSessionRevoked });

  useEffect(() => {
    applyBranding(me.tenant?.branding ?? null);
  }, [me.tenant]);

  useEffect(() => {
    const base = me.tenant?.branding.displayName ?? me.tenant?.name ?? 'Platform';
    document.title = `${pageTitleFor(nav, pathname)} · ${base} · Ankyah CRM`;
  }, [nav, pathname, me.tenant]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const toggleSidebar = useCallback(() => {
    setCollapsed((c) => {
      safeStorage.set(SIDEBAR_KEY, c ? 'expanded' : 'collapsed');
      return !c;
    });
  }, []);

  const handleSignOut = useCallback(() => {
    if (me.support) void support.end();
    else void signOut();
  }, [me.support, signOut, support]);

  return (
    <div className="flex h-dvh overflow-hidden bg-bg">
      <a
        href="#main"
        className="focus-ring sr-only z-50 rounded-md bg-surface px-3 py-2 text-sm font-medium shadow-e2 focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
      >
        Skip to content
      </a>

      <Sidebar nav={nav} me={me} collapsed={collapsed} onToggle={toggleSidebar} className="hidden lg:flex" />

      <DialogPrimitive.Root open={mobileOpen} onOpenChange={setMobileOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[rgb(6_22_24/0.45)] data-[state=open]:animate-[fade-in_160ms_ease-out] lg:hidden" />
          <DialogPrimitive.Content className="fixed inset-y-0 left-0 z-50 shadow-e3 data-[state=open]:animate-[drawer-left-in_260ms_var(--ease-out-soft)] lg:hidden">
            <DialogPrimitive.Title className="sr-only">Navigation</DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">Primary navigation</DialogPrimitive.Description>
            <Sidebar nav={nav} me={me} collapsed={false} onNavigate={() => setMobileOpen(false)} />
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      <motion.div layout="position" className="flex min-w-0 flex-1 flex-col">
        {me.support && (
          <SupportBanner
            tenantName={me.tenant?.branding.displayName ?? me.tenant?.name ?? 'this organisation'}
            expiresAt={me.support.expiresAt}
            onEnd={() => void support.end()}
          />
        )}
        <Topbar
          me={me}
          nav={nav}
          onOpenPalette={() => setPaletteOpen(true)}
          onOpenMobileNav={() => setMobileOpen(true)}
          onSignOut={handleSignOut}
        />
        <main id="main" tabIndex={-1} className="flex-1 overflow-y-auto focus:outline-none">
          <PageTransition key={pathname} className="mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-8 sm:py-8">
            <Outlet />
          </PageTransition>
        </main>
      </motion.div>

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        nav={nav}
        onSignOut={handleSignOut}
        supportMode={Boolean(me.support)}
      />
      <SessionSwitchOverlay message={switching} />
    </div>
  );
}
